import type { HTMLAttributes, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/lib/ui/cn'

interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** `section` panels with a title are named regions (aria-labelledby the title). */
  as?: 'section' | 'div' | 'article'
  icon?: ReactNode
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** A warm rim of light along the top edge, for the panel that matters most. */
  lit?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  bodyClassName?: string
}

const PADDING = { none: '', sm: 'p-4', md: 'p-4 sm:p-5', lg: 'p-5 sm:p-7' } as const

/** A card on the camp's glass: optional icon, title, context line and actions over a body. */
export function Panel({
  as: Tag = 'section',
  icon,
  title,
  description,
  actions,
  lit = false,
  padding = 'md',
  className,
  bodyClassName,
  children,
  ...props
}: PanelProps) {
  const headingId = useId()
  const labelled = Tag === 'section' && title ? { 'aria-labelledby': headingId } : {}
  return (
    <Tag className={cn('bl-panel', lit && 'bl-panel-lit', PADDING[padding], className)} {...labelled} {...props}>
      {title || actions ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {icon ? (
              <span aria-hidden="true" className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[color:var(--bl-gold-wash)] text-[color:var(--bl-gold-text)] ring-1 ring-[rgb(var(--color-primary)/0.22)]">
                {icon}
              </span>
            ) : null}
            <div className="min-w-0">
              {title ? (
                <h2 id={headingId} className="text-base font-semibold leading-snug text-[color:var(--bl-ink)]">
                  {title}
                </h2>
              ) : null}
              {description ? <p className="mt-1 text-sm leading-relaxed text-[color:var(--bl-ink-2)]">{description}</p> : null}
            </div>
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </Tag>
  )
}
